// Unified Benchmarking Page with Tabs
// Single page that combines benchmark list, details, and analytics with role-based tab visibility

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { DynamicForm } from '@/components/common/DynamicForm';
import { benchmarkEntity } from '@/config/entities/benchmarks';
import { createBenchmarkEntryEntity } from '@/config/entities/benchmarkEntriesEnhanced';
import { benchmarkService } from '@/services/benchmarksCRUD';
import { benchmarkEntriesAPI } from '@/services/benchmarkEntriesAPI';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { 
  DocumentArrowUpIcon,
  PlusIcon,
  CogIcon,
  InformationCircleIcon,
  ListBulletIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline';

export const BenchmarkingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [benchmark, setBenchmark] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editingEntry, setEditingEntry] = useState(false);
  const [entriesRefreshKey, setEntriesRefreshKey] = useState(0);

  // Create a static entity for benchmark entries (without hierarchy options)
  const staticBenchmarkEntryEntity = createBenchmarkEntryEntity();

  // Entries table uses correct API: GET/DELETE /benchmarking/:id/entries (Chunk 11)
  const entriesService = React.useMemo(() => (id ? {
    getAll: (params?: { page?: number; limit?: number; search?: string }) =>
      benchmarkEntriesAPI.getList(id, params),
    create: (data: any) => benchmarkEntriesAPI.create(id, data),
    update: (entryId: string, data: any) => benchmarkEntriesAPI.update(entryId, data),
    delete: (entryId: string) => benchmarkEntriesAPI.delete(entryId)
  } : null), [id]);

  const formatScopeIds = (val: string | unknown): string => {
    if (typeof val !== 'string') return '—';
    try {
      const arr = JSON.parse(val);
      return Array.isArray(arr) ? `${arr.length} selected` : val;
    } catch {
      return val;
    }
  };

  useEffect(() => {
    if (id) {
      setActiveTab('details');
      loadBenchmark();
    } else {
      setActiveTab('list');
      setLoading(false);
    }
  }, [id]);


  const loadBenchmark = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const benchmarkData = await benchmarkService.getById(id);
      setBenchmark(benchmarkData);
    } catch (error) {
      console.error('Error loading benchmark:', error);
      setBenchmark(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setShowCreateModal(true);
  };

  const handleView = (item: any) => {
    navigate(`/benchmarking/${item.id}`);
  };


  const handleCreateSubmit = async (data: any) => {
    setLoading(true);
    try {
      if (id) {
        await benchmarkEntriesAPI.create(id, data);
        setEntriesRefreshKey((k) => k + 1);
      } else {
        // Create benchmark: map form data to backend camelCase and scope arrays (REQ-BM-020)
        const payload = {
          name: data.name,
          description: data.description,
          benchmarkYear: data.benchmark_year ?? data.benchmarkYear,
          startDate: data.start_date ?? data.startDate,
          endDate: data.end_date ?? data.endDate,
          currency: data.currency ?? 'USD',
          fteDefinition: data.fte_definition ?? data.fteDefinition,
          scopeBusinessIds: Array.isArray(data.scopeBusinessIds) ? data.scopeBusinessIds : [],
          scopeGeographyIds: Array.isArray(data.scopeGeographyIds) ? data.scopeGeographyIds : [],
          scopeFunctionIds: Array.isArray(data.scopeFunctionIds) ? data.scopeFunctionIds : [],
          scopeProcessIds: Array.isArray(data.scopeProcessIds) ? data.scopeProcessIds : []
        };
        await benchmarkService.create(payload);
      }
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (data: any) => {
    setLoading(true);
    try {
      if (editingEntry) {
        await benchmarkEntriesAPI.update(editingItem.id, data);
        setEntriesRefreshKey((k) => k + 1);
      } else {
        await benchmarkService.update(editingItem.id, data);
      }
      setShowEditModal(false);
      setEditingItem(null);
      setEditingEntry(false);
    } catch (error) {
      console.error('Error updating:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: any) => {
    setEditingEntry(false);
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleEntryEdit = (item: any) => {
    setEditingEntry(true);
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleDelete = async (item: any) => {
            
    if (confirm(`Are you sure you want to delete the benchmark "${item.name}"? This will also delete all related benchmark entries.`)) {
      try {
        setLoading(true);
                await benchmarkService.delete(item.id);
        // The FlexibleCRUDTable will automatically refresh its data
        toast.success('Benchmark deleted successfully');
      } catch (error) {
        console.error('Error deleting benchmark:', error);
        toast.error('Failed to delete benchmark');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      setLoading(true);
      const newStatus = isActive ? 'ACTIVE' : 'DRAFT';
      await benchmarkService.update(id, { status: newStatus });
      toast.success(`Benchmark status changed to ${newStatus}`);
    } catch (error) {
      console.error('Error changing benchmark status:', error);
      toast.error('Failed to change benchmark status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingItem(null);
    setEditingEntry(false);
    setLoading(false);
  };

  // Role-based tab visibility
  const getVisibleTabs = () => {
    // Debug: Log user role for troubleshooting
            
    const allTabs = [
      {
        id: 'list',
        name: t('benchmarking.benchmarksList') || 'Benchmarks List',
        icon: ListBulletIcon,
        visible: true // Everyone can see the list
      },
      {
        id: 'details',
        name: t('benchmarking.benchmarkDetails') || 'Benchmark Details',
        icon: InformationCircleIcon,
        visible: !!id // Only visible when viewing a specific benchmark
      },
      {
        id: 'entries',
        name: t('benchmarking.benchmarkEntries') || 'Benchmark Entries',
        icon: DocumentArrowUpIcon,
        visible: !!id // Visible to all users when viewing a specific benchmark
      },
      {
        id: 'analytics',
        name: t('benchmarking.analytics') || 'Analytics',
        icon: ChartPieIcon,
        visible: !!id && (
          user?.role === 'PLATFORM_ADMIN' || 
          user?.role === 'TENANT_ADMIN' || 
          user?.role === 'T_ADMIN' ||
          user?.role === 'C_ADMIN' ||
          user?.role === 'P_ADMIN' ||
          user?.role === 'ANALYST' ||
          user?.email?.includes('@platform.com') // Platform admin check
        )
      },
      {
        id: 'propagation',
        name: t('benchmarking.hierarchyPropagation') || 'Hierarchy Propagation',
        icon: DocumentArrowUpIcon,
        visible: !!id && (
          user?.role === 'PLATFORM_ADMIN' || 
          user?.role === 'TENANT_ADMIN' || 
          user?.role === 'T_ADMIN' ||
          user?.role === 'C_ADMIN' ||
          user?.role === 'P_ADMIN' ||
          user?.role === 'MANAGER' ||
          user?.email?.includes('@platform.com') // Platform admin check
        )
      },
      {
        id: 'settings',
        name: t('benchmarking.settings') || 'Settings',
        icon: CogIcon,
        visible: !!id && (
          user?.role === 'PLATFORM_ADMIN' || 
          user?.role === 'TENANT_ADMIN' || 
          user?.role === 'T_ADMIN' ||
          user?.role === 'C_ADMIN' ||
          user?.role === 'P_ADMIN' ||
          user?.email?.includes('@platform.com') // Platform admin check
        )
      }
    ];

    return allTabs.filter(tab => tab.visible);
  };

  const visibleTabs = getVisibleTabs();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="mb-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">
{id ? benchmark?.name || t('benchmarking.benchmarkDetails') : t('benchmarking.benchmarking')}
              </h1>
              <p className="text-xs text-[var(--color-secondary-text)]">
{id ? benchmark?.description : t('benchmarking.description')}
              </p>
            </div>
            {!id && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/admin/process-admin')}
                  className="inline-flex items-center px-4 py-2 border border-[var(--color-border)] text-sm font-medium rounded-md text-[var(--color-surface-text)] bg-[var(--color-surface-bg)] hover:bg-[var(--color-secondary-bg)]"
                >
                  <CogIcon className="h-4 w-4 mr-2" />
                  Process admin
                </button>
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('benchmarking.createBenchmark')}
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-[var(--color-border)]">
            <nav className="-mb-px flex space-x-8">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-[var(--color-info)] text-[var(--color-info)]'
                      : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-info)] hover:border-[var(--color-info)]'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] mt-2">
            {activeTab === 'list' && (
              <div className="p-2">
                <FlexibleCRUDTable
                  entity={benchmarkEntity}
                  service={benchmarkService}
                  onCreate={handleCreate}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
searchPlaceholder={t('benchmarking.searchBenchmarks')}
                  showCreateButton={true}
                  showSearch={true}
                  showPagination={true}
                  pageSize={10}
                  className="w-full"
                />
              </div>
            )}

            {activeTab === 'details' && id && benchmark && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-[var(--color-secondary-bg)] p-4 rounded-lg border border-[var(--color-border)]">
                    <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-2">{t('benchmarking.basicInformation')}</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium text-[var(--color-secondary-text)]">{t('benchmarking.name')}</dt>
                        <dd className="text-sm text-[var(--color-surface-text)]">{benchmark.name}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-[var(--color-secondary-text)]">{t('benchmarking.year')}</dt>
                        <dd className="text-sm text-[var(--color-surface-text)]">{benchmark.benchmark_year}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-[var(--color-secondary-text)]">{t('benchmarking.status')}</dt>
                        <dd className="text-sm text-[var(--color-surface-text)]">{benchmark.status}</dd>
                      </div>
                      {benchmark.currency && (
                        <div>
                          <dt className="text-sm font-medium text-[var(--color-secondary-text)]">{t('benchmarking.currency')}</dt>
                          <dd className="text-sm text-[var(--color-surface-text)]">{benchmark.currency}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                  <div className="bg-[var(--color-secondary-bg)] p-4 rounded-lg border border-[var(--color-border)]">
                    <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-2">{t('benchmarking.quickStats')}</h3>
                    <dl className="space-y-2">
                      <div>
                        <dt className="text-sm font-medium text-[var(--color-secondary-text)]">{t('benchmarking.totalEntries')}</dt>
                        <dd className="text-sm text-[var(--color-surface-text)]">{benchmark.total_entries ?? '—'}</dd>
                      </div>
                    </dl>
                  </div>
                  {(benchmark.scope_business_ids || benchmark.scope_geography_ids || benchmark.scope_function_ids || benchmark.scope_process_ids) && (
                    <div className="bg-[var(--color-secondary-bg)] p-4 rounded-lg border border-[var(--color-border)]">
                      <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-2">Scope</h3>
                      <dl className="space-y-2 text-sm text-[var(--color-surface-text)]">
                        {benchmark.scope_business_ids && <div>Business: {formatScopeIds(benchmark.scope_business_ids)}</div>}
                        {benchmark.scope_geography_ids && <div>Geography: {formatScopeIds(benchmark.scope_geography_ids)}</div>}
                        {benchmark.scope_function_ids && <div>Function: {formatScopeIds(benchmark.scope_function_ids)}</div>}
                        {benchmark.scope_process_ids && <div>Processes: {formatScopeIds(benchmark.scope_process_ids)}</div>}
                      </dl>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'entries' && id && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
<h3 className="text-lg font-medium text-[var(--color-surface-text)]">{t('benchmarking.benchmarkEntries')}</h3>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-[var(--color-primary-text)] bg-[var(--color-primary-bg)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
{t('benchmarking.addEntry')}
                  </button>
                </div>
                <FlexibleCRUDTable
                  key={`entries-${id}-${entriesRefreshKey}`}
                  entity={staticBenchmarkEntryEntity}
                  service={entriesService!}
                  onCreate={() => setShowCreateModal(true)}
                  onEdit={handleEntryEdit}
                  searchPlaceholder={t('benchmarking.searchEntries')}
                  showCreateButton={false}
                  showSearch={true}
                  showPagination={true}
                  pageSize={10}
                  className="w-full"
                />
              </div>
            )}

            {activeTab === 'analytics' && id && (
              <div className="p-6">
<h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-4">{t('benchmarking.analytics')}</h3>
                <div className="text-center py-8 text-[var(--color-secondary-text)]">
                  <ChartPieIcon className="h-12 w-12 mx-auto mb-4" />
<p>{t('benchmarking.analyticsComingSoon')}</p>
                </div>
              </div>
            )}

            {activeTab === 'propagation' && id && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
<h3 className="text-lg font-medium text-[var(--color-surface-text)]">{t('benchmarking.hierarchyDataPropagation')}</h3>
                  <button
                    onClick={() => navigate(`/benchmarking/${id}/propagation`)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-[var(--color-primary-text)] bg-[var(--color-primary-bg)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]"
                  >
                    <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
{t('benchmarking.openPropagationPage')}
                  </button>
                </div>
                <div className="text-center py-8 text-[var(--color-secondary-text)]">
                  <DocumentArrowUpIcon className="h-12 w-12 mx-auto mb-4" />
<p>{t('benchmarking.reviewAndPropagateData')}</p>
<p className="text-sm mt-2">{t('benchmarking.clickOpenPropagationPage')}</p>
                </div>
              </div>
            )}

            {activeTab === 'settings' && id && (
              <div className="p-6">
<h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-4">{t('benchmarking.settings')}</h3>
                <div className="text-center py-8 text-[var(--color-secondary-text)]">
                  <CogIcon className="h-12 w-12 mx-auto mb-4" />
<p>{t('benchmarking.settingsComingSoon')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-[var(--color-overlay)] overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-[var(--color-surface-bg)] border-[var(--color-border)]">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-4">
{id ? t('benchmarking.addEntry') : t('benchmarking.createBenchmark')}
              </h3>
              <DynamicForm
                key={id ? 'entry-form' : 'benchmark-form'}
                entity={id ? staticBenchmarkEntryEntity : benchmarkEntity}
                onSubmit={handleCreateSubmit}
                onCancel={handleCancel}
                submitLabel="Create"
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (benchmark or entry) */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 bg-[var(--color-overlay)] overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-[var(--color-surface-bg)] border-[var(--color-border)]">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-4">
                {editingEntry ? (t('benchmarking.editEntry') || 'Edit entry') : t('benchmarking.editBenchmark')}
              </h3>
              <DynamicForm
                entity={editingEntry ? staticBenchmarkEntryEntity : benchmarkEntity}
                initialData={editingItem}
                onSubmit={handleEditSubmit}
                onCancel={handleCancel}
                submitLabel="Update"
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};